var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@mui/system";
import { Box, Card, CardActionArea, CardContent, CardMedia, Grid, IconButton, Skeleton } from "@mui/material";
import { useOnWindowResize } from "../../hooks/useOnWindowResize";
import { getTimeElapsedInStopWatchFormatString } from "../../helpers/GetTimeElapsedFormatted.helper";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import { defaultTheme } from "../../themes/default.theme";
import { AudioRecorderManager } from "../../utils/AudioRecorderManager";
var StyledCard = styled(Card)(function () { return ({
    borderRadius: 15,
    maxWidth: "400px",
}); });
export default forwardRef(function (props, ref) {
    var sx = props.sx;
    return (_jsx(StyledCard, __assign({ ref: ref, elevation: 5, sx: sx }, { children: _jsx(CardContentComponentWrapper, __assign({}, props)) })));
});
export var ERecordingAndPlaybackState;
(function (ERecordingAndPlaybackState) {
    ERecordingAndPlaybackState[ERecordingAndPlaybackState["PRE_RECORDING"] = 0] = "PRE_RECORDING";
    ERecordingAndPlaybackState[ERecordingAndPlaybackState["RECORDING"] = 1] = "RECORDING";
    ERecordingAndPlaybackState[ERecordingAndPlaybackState["PLAYBACK"] = 2] = "PLAYBACK";
})(ERecordingAndPlaybackState || (ERecordingAndPlaybackState = {}));
var SOUND_WAVE_OSCILLATOR_CANVAS_ID = "sound-wave-oscillator";
// Card content for the visuals and layout of the VTCard
function CardContentComponentWrapper(_a) {
    var sx = _a.sx, loading = _a.loading, onRecordingFinished = _a.onRecordingFinished, onRecordingPlaybackStateChanged = _a.onRecordingPlaybackStateChanged;
    var _b = __read(useOnWindowResize(), 2), windowWidth = _b[0], _ = _b[1];
    var _c = __read(useState(null), 2), cardMediaNode = _c[0], setCardMediaNode = _c[1];
    var CardMediaRef = useCallback(function (node) {
        setCardMediaNode(node);
    }, []);
    var _d = __read(useState(150 - 80), 2), cardMediaHeight = _d[0], setCardMediaHeight = _d[1];
    useEffect(function () {
        if (cardMediaNode) {
            var width = cardMediaNode.clientWidth;
            if (185 > width) {
                setCardMediaHeight(150 - 50);
            }
            else if (250 > width) {
                setCardMediaHeight(200 - 80);
            }
            else if (350 > width) {
                setCardMediaHeight(240 - 80);
            }
            else {
                setCardMediaHeight(280 - 90);
            }
        }
    }, [cardMediaNode, sx, windowWidth]);
    var timeElapsedBoxRef = useRef();
    var animationFrameRequestIdTimeElapsed = useRef(0);
    useEffect(function () {
        return function () {
            window.cancelAnimationFrame(animationFrameRequestIdTimeElapsed.current);
        };
    }, []);
    var _e = __read(useState(ERecordingAndPlaybackState.PRE_RECORDING), 2), recordingPlaybackState = _e[0], setRecordingPlaybackState = _e[1];
    useEffect(function () {
        onRecordingPlaybackStateChanged && onRecordingPlaybackStateChanged(recordingPlaybackState);
    }, [recordingPlaybackState]);
    var audioRecorderManager = useRef(null);
    var _f = __read(useState(), 2), recordedAudioUrl = _f[0], setRecordedAudioUrl = _f[1];
    var canvasRef = useCallback(function (canvas) {
        if (canvas) {
            var rect = canvas.parentNode.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
            audioRecorderManager.current = new AudioRecorderManager(SOUND_WAVE_OSCILLATOR_CANVAS_ID, function (audioBlob) {
                onRecordingFinished && onRecordingFinished(audioBlob);
            });
        }
    }, []);
    var resizingDelayTimer = useRef(undefined);
    useEffect(function () {
        var waveFormContainer = document.getElementById("waveform");
        var resizeObserver = new ResizeObserver(function () {
            window.clearTimeout(resizingDelayTimer.current);
            resizingDelayTimer.current = window.setTimeout(function () {
                // check if the observed div is still mounted
                // else this will cause memory leak
                if (waveFormContainer) {
                    // waveFormContainer!.innerHTML = "";
                    // setWaveSurferObjState(waveFormContainer!);
                    var canvas = document.getElementById(SOUND_WAVE_OSCILLATOR_CANVAS_ID);
                    if (canvas) {
                        var rect = waveFormContainer.getBoundingClientRect();
                        canvas.width = rect.width;
                        canvas.height = rect.height;
                        audioRecorderManager.current = new AudioRecorderManager(SOUND_WAVE_OSCILLATOR_CANVAS_ID, function (audioBlob) {
                            onRecordingFinished && onRecordingFinished(audioBlob);
                        });
                    }
                }
            }, 100);
        });
        resizeObserver.observe(waveFormContainer);
    }, []);
    var buttons;
    if (recordingPlaybackState === ERecordingAndPlaybackState.PRE_RECORDING) {
        buttons = (_jsx(IconButton, __assign({ sx: {
                color: "red",
                background: defaultTheme.palette.background.default,
                "&:hover": {
                    color: "red",
                    background: defaultTheme.palette.background.default,
                },
            }, 
            // variant="outlined"
            onClick: function () {
                var _a;
                setRecordingPlaybackState(ERecordingAndPlaybackState.RECORDING);
                var startTime = Date.now();
                (_a = audioRecorderManager.current) === null || _a === void 0 ? void 0 : _a.startRecording();
                function step() {
                    // @ts-ignore
                    timeElapsedBoxRef.current.innerHTML = getTimeElapsedInStopWatchFormatString(Date.now() - startTime);
                    animationFrameRequestIdTimeElapsed.current = window.requestAnimationFrame(step);
                }
                animationFrameRequestIdTimeElapsed.current = window.requestAnimationFrame(step);
            } }, { children: _jsx(FiberManualRecordRoundedIcon, {}) })));
    }
    if (recordingPlaybackState === ERecordingAndPlaybackState.RECORDING) {
        buttons = (_jsx(IconButton, __assign({ sx: {
                color: "red",
                background: defaultTheme.palette.background.default,
                "&:hover": {
                    color: "red",
                    background: defaultTheme.palette.background.default,
                },
            }, onClick: function () {
                var _a;
                window.cancelAnimationFrame(animationFrameRequestIdTimeElapsed.current);
                (_a = audioRecorderManager.current) === null || _a === void 0 ? void 0 : _a.stopRecording();
                setRecordingPlaybackState(ERecordingAndPlaybackState.PLAYBACK);
            } }, { children: _jsx(StopCircleRoundedIcon, {}) })));
    }
    // if (recordingPlaybackState === RecordingAndPlaybackState.PLAYBACK) {
    //     buttons = (
    //         <IconButton sx={{}} onClick={() => {}}>
    //             <PlayCircleFilledRoundedIcon />
    //         </IconButton>
    //     );
    // }
    return (_jsxs(_Fragment, { children: [_jsx(CardActionArea, __assign({ disabled: loading }, { children: _jsx(CardMedia, __assign({ onClick: function () { }, ref: CardMediaRef, sx: {
                        height: "".concat(cardMediaHeight, "px"),
                        position: "relative",
                    } }, { children: !loading ? (_jsxs(Box, __assign({ component: "div", sx: {
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            width: "100%",
                        } }, { children: [_jsx(Box, __assign({ component: "div", id: "waveform", sx: {
                                    flex: 6,
                                    background: "#333",
                                    display: "flex",
                                } }, { children: _jsx("canvas", { ref: canvasRef, id: SOUND_WAVE_OSCILLATOR_CANVAS_ID }) })), _jsx(Box, __assign({ ref: timeElapsedBoxRef, component: "div", sx: {
                                    flex: 1,
                                    background: "#444",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    fontFamily: "Digit",
                                    fontSize: "1.2em",
                                    position: "relative",
                                } }, { children: getTimeElapsedInStopWatchFormatString(0) }))] }))) : (_jsx(Skeleton, { variant: "rectangular", sx: {
                            borderRadius: "0px",
                            width: "100%",
                            height: "100%",
                        } })) })) })), _jsx(CardContent, __assign({ sx: { ":last-child": { paddingBottom: "16px" } } }, { children: _jsx(Grid, __assign({ container: true }, { children: _jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center" }, { children: !loading ? (
                        // @ts-ignore
                        buttons) : (_jsx(Skeleton, { variant: "text", sx: { width: "75%", fontSize: "1.4em" } })) })) })) }))] }));
}
